import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { AlertCircle, Bookmark, Captions, Check, CheckCircle2, ChevronDown, Clock3, Download, Filter, Languages, LoaderCircle, MonitorPlay, RadioTower, Save, Search, SlidersHorizontal, Sparkles, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { DurationRangeFilter } from '../../components/DurationRangeFilter'
import { CONTENT_TAGS, CONTENT_TAG_ICONS, CONTENT_TYPES, CONTENT_TYPE_ICONS, ContentTypeSelect } from '../../components/ContentTypeSelect'
import { FilmDetailsDialog } from '../../components/FilmDetailsDialog'
import { PageState } from '../../components/PageState'
import { SenderLogo } from '../../components/SenderLogo'
import { ApiError, api, formatDuration, queryString, type ContentTag, type ContentType, type DownloadJob, type Facet, type Film, type MediaRoot, type Page, type SavedFilter, type SaveSubscription } from '../../lib/api'
import { sourceResolution } from '../../lib/media'
import { SubscriptionDialog } from '../subscriptions/SubscriptionsView'
import { BulkDownloadDialog, DownloadDialog } from './DownloadDialog'

interface Props { mediaRoots: MediaRoot[]; bookmarkView?: boolean }
interface ContextMenuState { film: Film; x: number; y: number }
interface CatalogFilterDefinition {
  query: string; sender: string; topic: string; subtitles: boolean; highQuality: boolean
  hideLivestreams: boolean; onlyNew: boolean; minimumMinutes: number | null; maximumMinutes: number | null
  excludeAudio: boolean; excludeSign: boolean; excludeOriginal: boolean; excludeBurnedSubtitles: boolean; bookmarkedOnly: boolean
  unseenOnly: boolean; dateFrom: string; dateTo: string; contentTypes: ContentType[]; contentTags: ContentTag[]
}
const column = createColumnHelper<Film>()
const CATALOG_PREFERENCES_KEY = 'meine-mediathek.catalog-filter-preferences.v1'

interface CatalogFilterPreferences {
  query: string; sender: string; topic: string
  subtitles: boolean; highQuality: boolean; hideLivestreams: boolean; onlyNew: boolean
  excludeAudio: boolean; excludeSign: boolean; excludeOriginal: boolean; excludeBurnedSubtitles: boolean; bookmarkedOnly: boolean; unseenOnly: boolean
  minimumMinutes: number | null; maximumMinutes: number | null; dateFrom: string; dateTo: string
  contentTypes: ContentType[]; contentTags: ContentTag[]
}

function loadCatalogPreferences(): CatalogFilterPreferences {
  const defaults: CatalogFilterPreferences = {
    query: '', sender: '', topic: '',
    subtitles: false, highQuality: false, hideLivestreams: true, onlyNew: false,
    excludeAudio: true, excludeSign: true, excludeOriginal: true, excludeBurnedSubtitles: true, bookmarkedOnly: false, unseenOnly: false,
    minimumMinutes: null, maximumMinutes: null, dateFrom: '', dateTo: '', contentTypes: [], contentTags: [],
  }
  try {
    const stored = JSON.parse(window.localStorage.getItem(CATALOG_PREFERENCES_KEY) ?? '{}') as Partial<CatalogFilterPreferences>
    return { ...defaults, ...stored }
  } catch {
    return defaults
  }
}

export function CatalogView({ mediaRoots, bookmarkView = false }: Props) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { filmId } = useParams<{ filmId?: string }>()
  const catalogPath = bookmarkView ? '/merkliste' : '/sendungen'
  const mobile = useMediaQuery('(max-width: 760px)')
  const [initialPreferences] = useState(loadCatalogPreferences)
  const [query, setQuery] = useState(initialPreferences.query)
  const [sender, setSender] = useState(initialPreferences.sender)
  const [topic, setTopic] = useState(initialPreferences.topic)
  const [subtitles, setSubtitles] = useState(initialPreferences.subtitles)
  const [highQuality, setHighQuality] = useState(initialPreferences.highQuality)
  const [hideLivestreams, setHideLivestreams] = useState(initialPreferences.hideLivestreams)
  const [onlyNew, setOnlyNew] = useState(initialPreferences.onlyNew)
  const [excludeAudio, setExcludeAudio] = useState(initialPreferences.excludeAudio)
  const [excludeSign, setExcludeSign] = useState(initialPreferences.excludeSign)
  const [excludeOriginal, setExcludeOriginal] = useState(initialPreferences.excludeOriginal)
  const [excludeBurnedSubtitles, setExcludeBurnedSubtitles] = useState(initialPreferences.excludeBurnedSubtitles)
  const [bookmarkedOnly, setBookmarkedOnly] = useState(initialPreferences.bookmarkedOnly)
  const [unseenOnly, setUnseenOnly] = useState(initialPreferences.unseenOnly)
  const [contentTypes, setContentTypes] = useState<ContentType[]>(initialPreferences.contentTypes)
  const [contentTags, setContentTags] = useState<ContentTag[]>(initialPreferences.contentTags)
  const [dateFrom, setDateFrom] = useState(initialPreferences.dateFrom)
  const [dateTo, setDateTo] = useState(initialPreferences.dateTo)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('')
  const [minimumMinutes, setMinimumMinutes] = useState<number | null>(initialPreferences.minimumMinutes)
  const [maximumMinutes, setMaximumMinutes] = useState<number | null>(initialPreferences.maximumMinutes)
  const [selected, setSelected] = useState<Film | null>(null)
  const [catalogEntryUnavailable, setCatalogEntryUnavailable] = useState(false)
  const [downloadFilm, setDownloadFilm] = useState<Film | null>(null)
  const [selectedFilms, setSelectedFilms] = useState<Map<string, Film>>(() => new Map())
  const [bulkDownloadOpen, setBulkDownloadOpen] = useState(false)
  const [subscriptionSeed, setSubscriptionSeed] = useState<Partial<SaveSubscription> | null>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)
  const parentRef = useRef<HTMLDivElement>(null)
  const effectiveBookmarkedOnly = bookmarkView || bookmarkedOnly
  useEffect(() => {
    window.localStorage.setItem(CATALOG_PREFERENCES_KEY, JSON.stringify({
      query, sender, topic, subtitles, highQuality, hideLivestreams, onlyNew,
      excludeAudio, excludeSign, excludeOriginal, excludeBurnedSubtitles, bookmarkedOnly, unseenOnly,
      minimumMinutes, maximumMinutes, dateFrom, dateTo, contentTypes, contentTags,
    } satisfies CatalogFilterPreferences))
  }, [query, sender, topic, subtitles, highQuality, hideLivestreams, onlyNew, excludeAudio, excludeSign,
    excludeOriginal, excludeBurnedSubtitles, bookmarkedOnly, unseenOnly, minimumMinutes, maximumMinutes, dateFrom, dateTo, contentTypes, contentTags])
  const activeFilterCount = [
    Boolean(sender), subtitles, highQuality, hideLivestreams, onlyNew,
    minimumMinutes != null, maximumMinutes != null, Boolean(topic),
    excludeAudio, excludeSign, excludeOriginal, excludeBurnedSubtitles, bookmarkedOnly, unseenOnly,
    Boolean(dateFrom), Boolean(dateTo), contentTypes.length > 0, contentTags.length > 0,
  ].filter(Boolean).length

  const closeContextMenu = useCallback(() => setContextMenu(null), [])
  const openDetails = useCallback((film: Film) => {
    setCatalogEntryUnavailable(false)
    setSelected(film)
    const target = `${catalogPath}/${encodeURIComponent(film.id)}`
    if (location.pathname !== target) navigate(target, { state: { catalogOverlay: true } })
  }, [catalogPath, location.pathname, navigate])
  const closeDetails = useCallback(() => {
    setSelected(null)
    if (!filmId) return
    const routeState = location.state as { catalogOverlay?: boolean } | null
    if (routeState?.catalogOverlay) navigate(-1)
    else navigate(catalogPath, { replace: true })
  }, [catalogPath, filmId, location.state, navigate])
  const setDurationRange = useCallback((minimum: number | null, maximum: number | null) => {
    setMinimumMinutes(minimum)
    setMaximumMinutes(maximum)
  }, [])
  useEffect(() => {
    if (!contextMenu) return
    window.addEventListener('pointerdown', closeContextMenu)
    window.addEventListener('blur', closeContextMenu)
    return () => {
      window.removeEventListener('pointerdown', closeContextMenu)
      window.removeEventListener('blur', closeContextMenu)
    }
  }, [contextMenu, closeContextMenu])

  const senders = useQuery({ queryKey: ['catalog', 'senders'], queryFn: () => api<Facet[]>('/api/v1/catalog/senders') })
  const savedFilters = useQuery({ queryKey: ['saved-filters'], queryFn: () => api<SavedFilter[]>('/api/v1/saved-filters') })
  const queryClient = useQueryClient()
  const saveFilter = useMutation({ mutationFn: ({ name, definition }: { name: string; definition: CatalogFilterDefinition }) => api<SavedFilter>('/api/v1/saved-filters', { method: 'POST', body: JSON.stringify({ name, definition }) }), onSuccess: (saved) => { setSelectedSavedFilter(saved.id); queryClient.invalidateQueries({ queryKey: ['saved-filters'] }) } })
  const deleteFilter = useMutation({ mutationFn: (id: string) => api<void>(`/api/v1/saved-filters/${id}`, { method: 'DELETE' }), onSuccess: () => { setSelectedSavedFilter(''); queryClient.invalidateQueries({ queryKey: ['saved-filters'] }) } })
  const downloads = useQuery({ queryKey: ['downloads'], queryFn: () => api<DownloadJob[]>('/api/v1/downloads?limit=1000'), refetchInterval: 15_000 })
  const selectedFilmQuery = useQuery({
    queryKey: ['catalog', 'detail', filmId],
    queryFn: () => api<Film>(`/api/v1/catalog/${encodeURIComponent(filmId!)}`),
    enabled: Boolean(filmId),
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 404) && failureCount < 1,
  })
  useEffect(() => {
    if (!filmId) {
      setSelected(null)
    } else if (selectedFilmQuery.data) {
      setCatalogEntryUnavailable(false)
      setSelected(selectedFilmQuery.data)
    } else if (selectedFilmQuery.error instanceof ApiError && selectedFilmQuery.error.status === 404) {
      setSelected(null)
      setCatalogEntryUnavailable(true)
      navigate(catalogPath, { replace: true })
    }
  }, [catalogPath, filmId, navigate, selectedFilmQuery.data, selectedFilmQuery.error])
  const downloadStates = useMemo(() => {
    const states = new Map<string, DownloadJob['state']>()
    downloads.data?.forEach((job) => { if (job.filmId && job.state !== 'DELETED' && job.state !== 'SKIPPED' && !states.has(job.filmId)) states.set(job.filmId, job.state) })
    return states
  }, [downloads.data])
  const filmsQuery = useInfiniteQuery({
    queryKey: ['catalog', 'sendungen', query, sender, topic, subtitles, highQuality, hideLivestreams, onlyNew, minimumMinutes, maximumMinutes, excludeAudio, excludeSign, excludeOriginal, effectiveBookmarkedOnly, unseenOnly, dateFrom, dateTo, contentTypes.join(','), contentTags.join(','), excludeBurnedSubtitles],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<Page<Film>>(`/api/v1/catalog?${queryString({
      query, sender, topic, subtitles: subtitles || undefined, highQuality: highQuality || undefined,
      livestream: hideLivestreams ? false : undefined,
      newFilm: onlyNew || undefined,
      audioDescription: excludeAudio ? false : undefined,
      signLanguage: excludeSign ? false : undefined,
      originalLanguage: excludeOriginal ? false : undefined,
      burnedInSubtitles: excludeBurnedSubtitles ? false : undefined,
      bookmarked: effectiveBookmarkedOnly ? true : undefined,
      seen: unseenOnly ? false : undefined,
      contentTypes: contentTypes.length ? contentTypes.join(',') : undefined,
      contentTags: contentTags.length ? contentTags.join(',') : undefined,
      dateFrom: dateFrom || undefined, dateTo: dateTo || undefined,
      minimumDurationSeconds: minimumMinutes == null ? undefined : minimumMinutes * 60,
      maximumDurationSeconds: maximumMinutes == null ? undefined : maximumMinutes * 60,
      page: pageParam, size: 50, sort: 'broadcastAt', direction: 'desc',
    })}`),
    getNextPageParam: (lastPage) => lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
  })
  const sendungen = useMemo(() => filmsQuery.data?.pages.flatMap((page) => page.items) ?? [], [filmsQuery.data])
  const total = filmsQuery.data?.pages[0]?.totalItems ?? 0
  const resultLabel = filmsQuery.isLoading
    ? t('sendungen.resultsLoading')
    : t('sendungen.results', { count: total.toLocaleString('de-DE') })
  const startSubscription = useCallback((film: Film) => {
    setSubscriptionSeed({
      name: film.topic || film.title, active: true, sender: film.sender, topic: film.topic,
      titleFilter: '', topicTitleFilter: '', anywhereFilter: '', filterMode: 'TEXT',
      minimumDurationSeconds: null, maximumDurationSeconds: null, onlyNew: false,
      excludeAudioDescription: true, excludeSignLanguage: true, excludeOriginalLanguage: false,
      executionMode: 'AUTO', quality: 'HIGH',
      mediaRootId: mediaRoots[0]?.id ?? 'downloads', destinationSubpath: '', subtitlesEnabled: true, infoFileEnabled: true,
    })
    setContextMenu(null)
  }, [mediaRoots])

  const filterDefinition = (): CatalogFilterDefinition => ({ query, sender, topic, subtitles, highQuality,
    hideLivestreams, onlyNew, minimumMinutes, maximumMinutes, excludeAudio, excludeSign, excludeOriginal, excludeBurnedSubtitles,
    bookmarkedOnly, unseenOnly, dateFrom, dateTo, contentTypes, contentTags })
  const applyFilter = (definition: Partial<CatalogFilterDefinition>) => {
    setQuery(definition.query ?? ''); setSender(definition.sender ?? ''); setTopic(definition.topic ?? '')
    setSubtitles(Boolean(definition.subtitles)); setHighQuality(Boolean(definition.highQuality))
    setHideLivestreams(definition.hideLivestreams !== false); setOnlyNew(Boolean(definition.onlyNew))
    setMinimumMinutes(definition.minimumMinutes ?? null); setMaximumMinutes(definition.maximumMinutes ?? null)
    setExcludeAudio(Boolean(definition.excludeAudio)); setExcludeSign(Boolean(definition.excludeSign))
    setExcludeOriginal(Boolean(definition.excludeOriginal)); setExcludeBurnedSubtitles(definition.excludeBurnedSubtitles !== false); setBookmarkedOnly(Boolean(definition.bookmarkedOnly))
    setUnseenOnly(Boolean(definition.unseenOnly)); setDateFrom(definition.dateFrom ?? ''); setDateTo(definition.dateTo ?? '')
    setContentTypes(definition.contentTypes?.filter((type) => CONTENT_TYPES.includes(type)) ?? [])
    setContentTags(definition.contentTags?.filter((tag) => CONTENT_TAGS.includes(tag)) ?? [])
  }
  const stateMutation = useMutation({
    mutationFn: ({ film, bookmarked, seen }: { film: Film; bookmarked: boolean; seen: boolean }) => api(`/api/v1/library-state/${film.id}`, { method: 'PUT', body: JSON.stringify({ bookmarked, seen, note: '' }) }),
    onSuccess: (_data, variables) => {
      const update = (film: Film) => film.id === variables.film.id
        ? { ...film, bookmarked: variables.bookmarked, seen: variables.seen }
        : film
      if (bookmarkView && !variables.bookmarked) closeDetails()
      else setSelected((current) => current?.id === variables.film.id ? update(current) : current)
      queryClient.getQueryCache().findAll({ queryKey: ['catalog', 'sendungen'] }).forEach((cached) => {
        const requiresBookmark = cached.queryKey[14] === true
        const requiresUnseen = cached.queryKey[15] === true
        const remove = (requiresBookmark && !variables.bookmarked) || (requiresUnseen && variables.seen)
        queryClient.setQueryData<InfiniteData<Page<Film>>>(cached.queryKey, (data) => data ? ({
          ...data,
          pages: data.pages.map((page) => {
            const contained = remove && page.items.some((film) => film.id === variables.film.id)
            const totalItems = contained ? Math.max(0, page.totalItems - 1) : page.totalItems
            return {
              ...page,
              totalItems,
              totalPages: totalItems === 0 ? 0 : Math.ceil(totalItems / page.size),
              items: page.items.map(update).filter((film) => !remove || film.id !== variables.film.id),
            }
          }),
        }) : data)
      })
      queryClient.invalidateQueries({ queryKey: ['catalog', 'sendungen'] })
    },
  })
  const classificationMutation = useMutation({
    mutationFn: ({ film, contentType, tags }: { film: Film; contentType: ContentType; tags: ContentTag[] }) => api<Film>(
      `/api/v1/catalog/${encodeURIComponent(film.id)}/classification`,
      { method: 'PUT', body: JSON.stringify({ contentType, tags }) },
    ),
    onSuccess: (updated) => {
      queryClient.getQueryCache().findAll({ queryKey: ['catalog', 'sendungen'] }).forEach((cached) => {
        queryClient.setQueryData<InfiniteData<Page<Film>>>(cached.queryKey, (data) => data ? ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((film) => film.id === updated.id ? updated : film),
          })),
        }) : data)
      })
      queryClient.setQueryData(['catalog', 'detail', updated.id], updated)
      setSelected((current) => current?.id === updated.id ? updated : current)
      setSelectedFilms((current) => {
        if (!current.has(updated.id)) return current
        const next = new Map(current)
        next.set(updated.id, updated)
        return next
      })
    },
  })
  const storeCurrentFilter = () => {
    const name = window.prompt(t('sendungen.savedFilterName'))?.trim()
    if (name) saveFilter.mutate({ name, definition: filterDefinition() })
  }
  const chooseSavedFilter = (id: string) => {
    setSelectedSavedFilter(id)
    const saved = savedFilters.data?.find((item) => item.id === id)
    if (saved) { applyFilter(saved.definition as unknown as CatalogFilterDefinition); setAdvancedOpen(true) }
  }
  const toggleSelection = useCallback((film: Film, checked: boolean) => {
    setSelectedFilms((current) => {
      const next = new Map(current)
      if (checked) next.set(film.id, film); else next.delete(film.id)
      return next
    })
  }, [])
  const specialVersionsHidden = excludeAudio && excludeSign && excludeOriginal && excludeBurnedSubtitles
  const toggleSpecialVersions = () => {
    const next = !specialVersionsHidden
    setExcludeAudio(next)
    setExcludeSign(next)
    setExcludeOriginal(next)
    setExcludeBurnedSubtitles(next)
  }
  const resetFilters = () => {
    setQuery(''); setSender(''); setTopic(''); setSubtitles(false); setHighQuality(false)
    setHideLivestreams(true); setOnlyNew(false); setMinimumMinutes(null); setMaximumMinutes(null)
    setExcludeAudio(true); setExcludeSign(true); setExcludeOriginal(true); setExcludeBurnedSubtitles(true); setBookmarkedOnly(false)
    setUnseenOnly(false); setDateFrom(''); setDateTo(''); setContentTypes([]); setContentTags([])
    setSelectedSavedFilter(''); setAdvancedOpen(false)
  }

  const columns = useMemo(() => [
    column.display({
      id: 'selection', header: '',
      cell: ({ row }) => <input className="row-selection" type="checkbox" checked={selectedFilms.has(row.original.id)} aria-label={`${t('sendungen.select')}: ${row.original.title}`} onClick={(event) => event.stopPropagation()} onChange={(event) => toggleSelection(row.original, event.target.checked)} />,
    }),
    column.accessor('sender', { header: t('sendungen.sender'), cell: ({ row }) => <SenderLogo sender={row.original.sender} compact /> }),
    column.display({
      id: 'classification', header: t('sendungen.classification.shortLabel'),
      cell: ({ row }) => <ContentTypeSelect film={row.original} compact disabled={classificationMutation.isPending} onChange={(contentType, tags) => classificationMutation.mutate({ film: row.original, contentType, tags })} />,
    }),
    column.accessor('topic', { header: t('sendungen.topic') }),
    column.accessor('title', {
      header: t('sendungen.titleColumn'),
      cell: ({ row }) => <div className="catalog-title-cell"><span>{row.original.title}</span></div>,
    }),
    column.display({ id: 'libraryState', header: t('sendungen.libraryState'), cell: ({ row }) => <LibraryStateBadges film={row.original} /> }),
    column.accessor('broadcastAt', {
      header: t('sendungen.date'), cell: (info) => info.getValue() ? new Date(info.getValue()!).toLocaleDateString('de-DE') : '—',
    }),
    column.accessor('durationSeconds', { header: t('sendungen.duration'), cell: (info) => formatDuration(info.getValue()) }),
    column.display({
      id: 'features', header: t('sendungen.features'),
      cell: ({ row }) => {
        const resolution = sourceResolution(row.original.highUrl)
        const recordedState = downloadStates.get(row.original.id)
        const downloadState = row.original.library.downloaded
          && recordedState !== 'QUEUED' && recordedState !== 'RUNNING'
          ? undefined
          : recordedState
        return <div className="feature-badges">
          {row.original.highQuality && <span className="quality-badge" title={resolution ? t('sendungen.knownResolution', { resolution }) : t('sendungen.unknownResolution')}><MonitorPlay /><b>{resolution ?? 'HQ'}</b></span>}
          {row.original.audioVariants.slice(0, 2).map((variant) => <span className="language-badge" key={variant.filmId} title={t('sendungen.includesAudioLanguage', { language: variant.label })}><Languages /><b>+ {variant.label}</b></span>)}
          {row.original.audioVariants.length > 2 && <span className="language-badge"><Languages /><b>+{row.original.audioVariants.length - 2}</b></span>}
          {row.original.subtitles && <span className="subtitle-badge" title={t('sendungen.hasSubtitles')}><Captions /><b>UT</b></span>}
          {row.original.newFilm && <span className="new-badge" title={t('sendungen.newInCatalog')}><Sparkles /><b>{t('sendungen.new')}</b></span>}
          <DownloadStateBadge state={downloadState} />
        </div>
      },
    }),
    column.display({
      id: 'actions', header: t('sendungen.actions'),
      cell: ({ row }) => <div className="row-actions">
        <button title={t('sendungen.createSubscription')} aria-label={`${t('sendungen.createSubscription')}: ${row.original.title}`} onClick={(event) => { event.stopPropagation(); startSubscription(row.original) }}><RadioTower /></button>
        <button className={`download ${row.original.library.downloaded ? 'present' : ''}`} title={downloadActionTitle(row.original, t)} aria-label={`${downloadActionTitle(row.original, t)}: ${row.original.title}`} onClick={(event) => { event.stopPropagation(); setDownloadFilm(row.original) }}>{row.original.library.downloaded ? <CheckCircle2 /> : <Download />}</button>
      </div>,
    }),
  ], [classificationMutation, downloadStates, selectedFilms, startSubscription, t, toggleSelection])
  const table = useReactTable({ data: sendungen, columns, getCoreRowModel: getCoreRowModel() })
  const rows = table.getRowModel().rows
  const virtualizer = useVirtualizer({ count: rows.length, getScrollElement: () => parentRef.current, estimateSize: () => mobile ? 148 : 48, overscan: 12 })
  const virtualRows = virtualizer.getVirtualItems()
  const lastVirtualIndex = virtualRows[virtualRows.length - 1]?.index ?? -1
  useEffect(() => virtualizer.measure(), [mobile, virtualizer])
  useEffect(() => {
    if (lastVirtualIndex >= rows.length - 15 && filmsQuery.hasNextPage && !filmsQuery.isFetchingNextPage) filmsQuery.fetchNextPage()
  }, [lastVirtualIndex, rows.length, filmsQuery.hasNextPage, filmsQuery.isFetchingNextPage, filmsQuery.fetchNextPage])

  return <section className="page catalog-page">
    {bookmarkView && <div className="bookmark-intro panel"><Bookmark /><div><strong>{t('sendungen.bookmarkIntroTitle')}</strong><p>{t('sendungen.bookmarkViewHelp')}</p></div></div>}
    <div className="catalog-toolbar panel redesigned-toolbar">
      <DebouncedSearchInput value={query} onChange={setQuery} placeholder={t('sendungen.searchPlaceholder')} />
      <button type="button" className={`mobile-filter-toggle ${mobileFiltersOpen ? 'open' : ''}`} aria-expanded={mobileFiltersOpen} onClick={() => setMobileFiltersOpen((open) => !open)}><Filter /><span>{mobileFiltersOpen ? t('sendungen.hideFilters') : t('sendungen.showFilters')}</span>{activeFilterCount > 0 && <b>{activeFilterCount}</b>}<small>{resultLabel}</small><ChevronDown /></button>
      <div className={`catalog-filter-region ${mobileFiltersOpen ? 'open' : ''}`}>
        <label className="compact-select sender-select"><span>{t('sendungen.sender')}</span><select value={sender} onChange={(event) => setSender(event.target.value)}><option value="">{t('common.all')}</option>{senders.data?.map((item) => <option key={item.value} value={item.value}>{item.value} · {item.count.toLocaleString('de-DE')}</option>)}</select></label>
        <div className="filter-controls">
        <FilterChip active={subtitles} onClick={() => setSubtitles(!subtitles)} label={t('sendungen.onlySubtitles')} />
        <FilterChip active={highQuality} onClick={() => setHighQuality(!highQuality)} label={t('sendungen.onlyHighQuality')} />
        <FilterChip active={hideLivestreams} onClick={() => setHideLivestreams(!hideLivestreams)} label={t('sendungen.hideLivestreams')} />
        <FilterChip active={specialVersionsHidden} onClick={toggleSpecialVersions} label={t('sendungen.hideSpecialVersions')} tooltip={t('sendungen.hideSpecialVersionsHint')} />
        <ContentTypeFilter selectedTypes={contentTypes} selectedTags={contentTags} onTypesChange={setContentTypes} onTagsChange={setContentTags} />
        <button type="button" className={`filter-chip labeled ${advancedOpen ? 'active' : ''}`} onClick={() => setAdvancedOpen(!advancedOpen)}><Filter />{t('sendungen.moreFilters')}</button>
        <select className="saved-filter-select" value={selectedSavedFilter} onChange={(event) => chooseSavedFilter(event.target.value)}><option value="">{t('sendungen.savedFilters')}</option>{savedFilters.data?.map((filter) => <option value={filter.id} key={filter.id}>{filter.name}</option>)}</select>
        <button className="filter-icon-button" title={t('sendungen.saveFilter')} onClick={storeCurrentFilter}><Save /></button>
        {selectedSavedFilter && <button className="filter-icon-button danger" title={t('sendungen.deleteFilter')} onClick={() => window.confirm(t('sendungen.confirmDeleteFilter')) && deleteFilter.mutate(selectedSavedFilter)}><Trash2 /></button>}
        <DurationRangeFilter minimumMinutes={minimumMinutes} maximumMinutes={maximumMinutes} onChange={setDurationRange} label={t('sendungen.durationRange')} unlimited={t('sendungen.unlimited')} />
        <span className={`catalog-result-count ${filmsQuery.isLoading ? 'loading' : ''}`}>{resultLabel}</span>
        {advancedOpen && <div className="advanced-filter-panel">
          <label><span>{t('sendungen.topicFilter')}</span><input value={topic} onChange={(event) => setTopic(event.target.value)} /></label>
          <label><span>{t('sendungen.dateFrom')}</span><input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <label><span>{t('sendungen.dateTo')}</span><input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
          <FilterChip active={excludeAudio} onClick={() => setExcludeAudio(!excludeAudio)} label={t('sendungen.excludeAudio')} />
          <FilterChip active={excludeSign} onClick={() => setExcludeSign(!excludeSign)} label={t('sendungen.excludeSign')} />
          <FilterChip active={excludeOriginal} onClick={() => setExcludeOriginal(!excludeOriginal)} label={t('sendungen.excludeOriginal')} />
          <FilterChip active={excludeBurnedSubtitles} onClick={() => setExcludeBurnedSubtitles(!excludeBurnedSubtitles)} label={t('sendungen.excludeBurnedSubtitles')} />
          <FilterChip active={onlyNew} onClick={() => setOnlyNew(!onlyNew)} label={t('sendungen.onlyNew')} tooltip={t('sendungen.onlyNewHint')} />
          {!bookmarkView && <FilterChip active={bookmarkedOnly} onClick={() => setBookmarkedOnly(!bookmarkedOnly)} label={t('sendungen.onlyBookmarked')} />}
          <FilterChip active={unseenOnly} onClick={() => setUnseenOnly(!unseenOnly)} label={t('sendungen.onlyUnseen')} />
        </div>}
        </div>
      </div>
    </div>
    {catalogEntryUnavailable && <div className="inline-warning catalog-unavailable-notice" role="status">
      <AlertCircle /><span>{t('sendungen.catalogEntryUnavailable')}</span>
      <button type="button" onClick={() => setCatalogEntryUnavailable(false)} aria-label={t('common.close')}><X /></button>
    </div>}
    <div className="catalog-workspace">
      <div className="data-panel">
        {!filmsQuery.isLoading && sendungen.length > 0 && <div className="data-header">
          {table.getHeaderGroups()[0].headers.map((header) => <div className={`cell-${header.id}`} key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</div>)}
        </div>}
        <div ref={parentRef} className="virtual-scroll" onScroll={() => contextMenu && setContextMenu(null)}>
          {filmsQuery.isLoading && <CatalogLoadingState title={t('sendungen.loadingTitle')} description={t('sendungen.loadingHint')} />}
          {!filmsQuery.isLoading && sendungen.length === 0 && <PageState
            icon={bookmarkView ? <Bookmark /> : <SlidersHorizontal />}
            title={t(bookmarkView ? 'sendungen.bookmarkEmptyTitle' : 'sendungen.noResultsTitle')}
            description={t(bookmarkView ? 'sendungen.bookmarkEmpty' : 'sendungen.noResults')}
          >
            {bookmarkView
              ? <button className="primary-button" onClick={() => navigate('/sendungen')}><Search />{t('sendungen.browseCatalog')}</button>
              : <button className="quiet-button" onClick={resetFilters}><X />{t('sendungen.resetFilters')}</button>}
          </PageState>}
          <div className="virtual-content" style={{ height: `${virtualizer.getTotalSize()}px` }}>
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]
              return <div ref={virtualizer.measureElement} data-index={virtualRow.index} key={row.original.id} className={`data-row ${selected?.id === row.original.id ? 'selected' : ''}`}
                onClick={() => openDetails(row.original)} onDoubleClick={() => setDownloadFilm(row.original)}
                onContextMenu={(event) => { event.preventDefault(); openDetails(row.original); setContextMenu({ film: row.original, x: Math.min(event.clientX, window.innerWidth - 270), y: Math.min(event.clientY, window.innerHeight - 155) }) }}
                style={{ transform: `translateY(${virtualRow.start}px)` }}>
                {row.getVisibleCells().map((cell) => <div className={`cell-${cell.column.id}`} key={cell.id} title={cell.column.id === 'title' || cell.column.id === 'topic' ? String(cell.getValue() ?? '') : undefined}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>)}
              </div>
            })}
          </div>
          {filmsQuery.isFetchingNextPage && <div className="loading-more">{t('sendungen.loadMore')}</div>}
        </div>
      </div>
      {selected && <FilmDetailsDialog film={selected} libraryStatus={selected.library} stateUpdating={stateMutation.isPending} stateError={stateMutation.isError} classificationUpdating={classificationMutation.isPending} classificationError={classificationMutation.isError} onClassificationChange={(contentType, tags) => classificationMutation.mutate({ film: selected, contentType, tags })} onClose={closeDetails} onDownload={() => { const film = selected; closeDetails(); setDownloadFilm(film) }} onSubscribe={() => startSubscription(selected)} onBookmark={() => stateMutation.mutate({ film: selected, bookmarked: !selected.bookmarked, seen: selected.seen })} onSeen={() => stateMutation.mutate({ film: selected, bookmarked: selected.bookmarked, seen: !selected.seen })} />}
      {selectedFilms.size > 0 && <div className="panel bulk-action-bar"><strong>{t('sendungen.selectedCount', { count: selectedFilms.size })}</strong><button className="quiet-button" onClick={() => setSelectedFilms(new Map())}>{t('sendungen.clearSelection')}</button><button className="primary-button" onClick={() => setBulkDownloadOpen(true)}><Download />{t('sendungen.downloadSelected')}</button></div>}
    </div>
    {contextMenu && <div className="row-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }} onPointerDown={(event) => event.stopPropagation()}>
      <strong>{contextMenu.film.title}</strong>
      <button onClick={() => { const film = contextMenu.film; setContextMenu(null); setDownloadFilm(film) }}><Download />{t('sendungen.download')}</button>
      <button onClick={() => startSubscription(contextMenu.film)}><RadioTower />{t('sendungen.createSubscription')}</button>
      <small>{t('sendungen.contextHint')}</small>
    </div>}
    {downloadFilm && <DownloadDialog film={downloadFilm} mediaRoots={mediaRoots} onClose={() => setDownloadFilm(null)} />}
    {bulkDownloadOpen && <BulkDownloadDialog films={[...selectedFilms.values()]} mediaRoots={mediaRoots} onClose={() => setBulkDownloadOpen(false)} onCompleted={() => setSelectedFilms(new Map())} />}
    {subscriptionSeed && <SubscriptionDialog subscription={null} initial={subscriptionSeed} mediaRoots={mediaRoots} onClose={() => setSubscriptionSeed(null)} />}
  </section>
}

function CatalogLoadingState({ title, description }: { title: string; description: string }) {
  return <div className="catalog-loading-state" role="status">
    <div className="catalog-loading-copy"><LoaderCircle /><div><strong>{title}</strong><span>{description}</span></div></div>
    <div className="catalog-loading-rows" aria-hidden="true">
      {Array.from({ length: 8 }, (_, index) => <div className="catalog-loading-row" key={index}>
        <i /><i /><i /><i /><i />
      </div>)}
    </div>
  </div>
}

const DebouncedSearchInput = memo(function DebouncedSearchInput({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) {
  const [input, setInput] = useState(value)
  useEffect(() => setInput(value), [value])
  useEffect(() => {
    const timer = window.setTimeout(() => onChange(input.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [input, onChange])
  return <div className="search-box"><Search /><input value={input} onChange={(event) => setInput(event.target.value)} placeholder={placeholder} />{input && <button aria-label="Suche leeren" onClick={() => setInput('')}><X /></button>}</div>
})

function LibraryStateBadges({ film }: { film: Film }) {
  const { t } = useTranslation()
  if (!film.bookmarked && !film.seen) return <span className="library-state-empty">—</span>
  return <div className="library-state-badges">
    {film.bookmarked && <span className="bookmarked"><Bookmark /><b>{t('sendungen.bookmarked')}</b></span>}
    {film.seen && <span className="seen"><Check /><b>{t('sendungen.seen')}</b></span>}
  </div>
}

function ContentTypeFilter({ selectedTypes, selectedTags, onTypesChange, onTagsChange }: { selectedTypes: ContentType[]; selectedTags: ContentTag[]; onTypesChange: (types: ContentType[]) => void; onTagsChange: (tags: ContentTag[]) => void }) {
  const { t } = useTranslation()
  const count = selectedTypes.length + selectedTags.length
  const toggleType = (type: ContentType) => onTypesChange(selectedTypes.includes(type) ? selectedTypes.filter((item) => item !== type) : [...selectedTypes, type])
  const toggleTag = (tag: ContentTag) => onTagsChange(selectedTags.includes(tag) ? selectedTags.filter((item) => item !== tag) : [...selectedTags, tag])
  return <details className="content-type-filter">
    <summary className={count ? 'active' : ''}><Filter /><span>{t('sendungen.contentTypes')}</span>{count > 0 && <b>{count}</b>}</summary>
    <div>
      <header><strong>{t('sendungen.classification.primaryForm')}</strong>{count > 0 && <button type="button" onClick={() => { onTypesChange([]); onTagsChange([]) }}>{t('sendungen.clearContentTypes')}</button>}</header>
      {CONTENT_TYPES.map((type) => {
        const Icon = CONTENT_TYPE_ICONS[type]
        return <label key={type}><input type="checkbox" checked={selectedTypes.includes(type)} onChange={() => toggleType(type)} /><Icon /><span>{t(`sendungen.classification.types.${type}`)}</span></label>
      })}
      <header className="content-tag-filter-heading"><strong>{t('sendungen.classification.additionalTags')}</strong></header>
      {CONTENT_TAGS.map((tag) => {
        const Icon = CONTENT_TAG_ICONS[tag]
        return <label key={tag}><input type="checkbox" checked={selectedTags.includes(tag)} onChange={() => toggleTag(tag)} /><Icon /><span>{t(`sendungen.classification.types.${tag}`)}</span></label>
      })}
    </div>
  </details>
}

function FilterChip({ active, onClick, label, tooltip }: { active: boolean; onClick: () => void; label: string; tooltip?: string }) {
  return <button type="button" className={`filter-chip labeled ${active ? 'active' : ''}`} aria-pressed={active} title={tooltip} onClick={onClick}>{label}</button>
}

function DownloadStateBadge({ state }: { state?: DownloadJob['state'] }) {
  const { t } = useTranslation()
  if (!state || state === 'DELETED' || state === 'UNSUPPORTED_SOURCE') return null
  const icon = state === 'COMPLETED' ? <CheckCircle2 /> : state === 'RUNNING' ? <LoaderCircle className="spin" /> : state === 'QUEUED' ? <Clock3 /> : <AlertCircle />
  return <span className={`film-download-state state-${state.toLowerCase()}`} title={t(`downloads.states.${state}`)}>{icon}<b>{t(`sendungen.downloadState.${state}`)}</b></span>
}

function downloadActionTitle(film: Film, t: (key: string, options?: Record<string, unknown>) => string) {
  if (!film.library.downloaded) return t('sendungen.download')
  const profiles = film.library.copies
    .map((copy) => copy.downloadProfileName || copy.mediaRootLabel)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(', ')
  return t('sendungen.downloadedAction', { profiles })
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const media = window.matchMedia(query)
    const update = () => setMatches(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [query])
  return matches
}
